import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';

export interface CameraPreviewHandle {
  capture: () => string | null;
}

export const CameraPreview = forwardRef<CameraPreviewHandle>(function CameraPreview(_props, ref) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' } })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
          setReady(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    capture: () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return null;
      const vw = video.videoWidth || 320;
      const vh = video.videoHeight || 240;
      const maxW = vh > vw ? 600 : 800;
      const maxH = vh > vw ? 800 : 600;
      const scale = Math.min(maxW / vw, maxH / vh, 1);
      canvas.width = Math.round(vw * scale);
      canvas.height = Math.round(vh * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    },
  }));

  return (
    <div className="relative mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-black">
      <video
        ref={videoRef}
        playsInline
        muted
        className="h-40 w-full object-cover"
        aria-label="Preview da câmera"
      />
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
          Iniciando câmera…
        </div>
      )}
    </div>
  );
});
